/**
 * PDF Generator Utility
 * Generates PDF reports for fire alarm projects
 */

export interface ProjectData {
  id: string;
  name: string;
  client?: string;
  location?: string;
  description?: string;
  devices?: Array<{
    id: string;
    name: string;
    type: string;
    quantity: number;
    standbyCurrentMa?: number;
    alarmCurrentMa?: number;
  }>;
  calculations?: Array<{
    id: string;
    type: string;
    name: string;
    result?: any;
    createdAt: string;
  }>;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PDFOptions {
  includeDeviceSchedule?: boolean;
  includeCalculations?: boolean;
  includeNotes?: boolean;
  includeCoverPage?: boolean;
  companyName?: string;
  companyLogo?: string;
  preparedBy?: string;
}

/**
 * Generate a PDF report for a project
 * Note: This is a simplified implementation. In production, use a library like jsPDF or pdfmake
 */
export async function generateProjectPDF(
  project: ProjectData,
  options: PDFOptions = {}
): Promise<Blob> {
  const {
    includeDeviceSchedule = true,
    includeCalculations = true,
    includeNotes = true,
    includeCoverPage = true,
    companyName = 'Fire Alarm Calculator',
    preparedBy = 'System Generated'
  } = options;

  // Build PDF content as text (simplified)
  let content = '';
  
  // Cover page
  if (includeCoverPage) {
    content += `
================================================================================
                              FIRE ALARM SYSTEM REPORT
================================================================================

Project: ${project.name}
${project.client ? `Client: ${project.client}` : ''}
${project.location ? `Location: ${project.location}` : ''}

Prepared By: ${preparedBy}
Company: ${companyName}
Date: ${new Date().toLocaleDateString()}

================================================================================

`;
  }

  // Project Overview
  content += `
PROJECT OVERVIEW
----------------
Name: ${project.name}
${project.description ? `Description: ${project.description}` : ''}
Created: ${new Date(project.createdAt).toLocaleDateString()}
Last Updated: ${new Date(project.updatedAt).toLocaleDateString()}

`;

  // Device Schedule
  if (includeDeviceSchedule && project.devices && project.devices.length > 0) {
    content += `
DEVICE SCHEDULE
---------------
`;
    let totalStandby = 0;
    let totalAlarm = 0;
    
    project.devices.forEach((device, index) => {
      const standby = (device.standbyCurrentMa || 0) * device.quantity;
      const alarm = (device.alarmCurrentMa || 0) * device.quantity;
      totalStandby += standby;
      totalAlarm += alarm;
      
      content += `
${index + 1}. ${device.name}
   Type: ${device.type}
   Quantity: ${device.quantity}
   Standby Current: ${standby.toFixed(2)} mA
   Alarm Current: ${alarm.toFixed(2)} mA
`;
    });
    
    content += `
TOTALS:
  Total Devices: ${project.devices.reduce((sum, d) => sum + d.quantity, 0)}
  Total Standby Current: ${totalStandby.toFixed(2)} mA
  Total Alarm Current: ${totalAlarm.toFixed(2)} mA

`;
  }

  // Calculations
  if (includeCalculations && project.calculations && project.calculations.length > 0) {
    content += `
CALCULATIONS
------------
`;
    project.calculations.forEach((calc, index) => {
      content += `
${index + 1}. ${calc.name}
   Type: ${calc.type}
   Date: ${new Date(calc.createdAt).toLocaleDateString()}
   ${calc.result ? `Result: ${JSON.stringify(calc.result, null, 2)}` : ''}
`;
    });
    content += '\n';
  }

  // Notes
  if (includeNotes && project.notes) {
    content += `
NOTES
-----
${project.notes}

`;
  }

  // Footer
  content += `
================================================================================
Generated by ${companyName} on ${new Date().toLocaleString()}
This report is for reference purposes only. All designs should be verified
by a licensed fire protection engineer before implementation.
================================================================================
`;

  // Create blob
  return new Blob([content], { type: 'text/plain' });
}

/**
 * Download a PDF file
 */
export function downloadPDF(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.txt') ? filename : `${filename}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate and download project PDF
 */
export async function generateAndDownloadProjectPDF(
  project: ProjectData,
  options?: PDFOptions
): Promise<void> {
  const blob = await generateProjectPDF(project, options);
  const filename = `${project.name.replace(/[^a-z0-9]/gi, '_')}_report`;
  downloadPDF(blob, filename);
}
